import { useSelector, useDispatch } from 'react-redux';
import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckIcon } from '@heroicons/react/20/solid';
import { fetchCart, removeFromCart } from '../../../../store/cart/cartReducer';
import Spinner from '../../../../components/Spinner/Index';

const CartPage = () => {
    const navigate = useNavigate();
    const token = useSelector((state) => state.auth.token);
    const { cart = {}, isLoading, isError } = useSelector((state) => state.cart);
    const { items = [] } = cart;
    const totalPrice = items.reduce((total, item) => total + item.packagePrice * item.quantity, 0);
    const dispatch = useDispatch();


    useEffect(() => {
        dispatch(fetchCart(token));
    }, [dispatch, token]);

    const handleRemoveItem = async (cartItemId, businessId) => {
        try {
            await dispatch(removeFromCart({ cartItemId, businessId }));
            dispatch(fetchCart(token)); // Refetch the cart after item removal
        } catch (error) {
            console.error("Failed to remove item from cart:", error);
        }
    };


    const handleCheckout = () => {
        if (items.length > 0) {
            navigate('/user/checkout');
        }
    };

    if (isLoading) {
        return <Spinner />;
    }

    if (isError) {
        return <div>Error loading cart. Please try again later.</div>;
    }

    return (
        <div className="bg-white">
            <main>
                <div className="mx-auto max-w-2xl px-4 py-16 sm:px-6 sm:py-24 lg:px-0">
                    <h1 className="text-center text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                        Shopping Cart
                    </h1>

                    {items.length === 0 ? (
                        <div className="mt-12 text-center">
                            <p className="text-lg text-gray-600">Your cart is empty.</p>
                            <Link
                                to="/user/shop"
                                className="mt-4 inline-block rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
                            >
                                Continue Shopping
                            </Link>
                        </div>
                    ) : (
                        <form className="mt-12">
                            <ul role="list" className="divide-y divide-gray-200 border-b border-t border-gray-200">
                                {items.map((item: any) => (
                                    <li key={item._id} className="flex py-6">
                                        <div className="shrink-0">
                                            <img
                                                alt={item.product?.image_url || 'Product image not available'}
                                                src={item.product?.image_url ? `/public/${item.product.image_url}` : 'https://placehold.co/600x400'}
                                                className="size-24 rounded-md object-cover sm:size-32"
                                            />
                                        </div>
                                        <div className="ml-4 flex flex-1 flex-col sm:ml-6">
                                            <div className="flex justify-between">
                                                <h4 className="text-lg font-medium text-gray-700 hover:text-gray-800">
                                                    {item.packageName}
                                                </h4>
                                                <p className="ml-4 text-sm font-medium text-gray-900">
                                                    ${item.packagePrice} x {item.quantity}
                                                </p>
                                            </div>
                                            <div className="flex items-center">
                                                <p className="text-md font-medium text-gray-500"> {item.businessId.businessInformation.businessName}</p>
                                            </div>
                                            <div className="mt-4 flex flex-1 items-end justify-between">
                                                <CheckIcon className="size-5 text-green-500" />
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveItem(item._id, item.businessId)}
                                                    className="text-sm font-medium text-green-600 hover:text-green-500"
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>

                            <div className="mt-10">
                                <p className="text-lg font-medium">Subtotal: ${totalPrice.toFixed(2)}</p>
                                <button
                                    type="button"
                                    onClick={handleCheckout}
                                    className={`mt-4 w-full rounded-md px-4 py-3 text-base font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${items.length > 0
                                        ? 'bg-green-600 hover:bg-green-700'
                                        : 'bg-gray-400 cursor-not-allowed'
                                        }`}
                                    disabled={items.length === 0}
                                >
                                    Checkout
                                </button>
                                <Link
                                    to="/user/shop"
                                    className="mt-4 w-full block rounded-md bg-gray-600 px-4 py-3 text-center text-base font-medium text-white shadow-sm hover:bg-gray-700"
                                >
                                    Continue Shopping
                                </Link>
                            </div>
                        </form>
                    )}
                </div>
            </main>
        </div>
    );
};

export default CartPage;
